import { useOrgContext } from '@/org/contexts/OrgContext'
import { CircleFragment, RoleFragment, useRolesSubscription } from '@gql'
import { useContext, useMemo } from 'react'
import { MeetingContext } from '../contexts/MeetingContext'

// The roles a Checklist or Indicators step shows, in the order it shows them.
// One list, shared by the two steps, because they display the same perimeter
// and differ only in the field they read (REU-04).
//
// ⚠️ The roles are subscribed to, not taken from the org context: OrgData holds
// RoleSummary, which carries a name and a colour but neither checklist nor
// indicators. The subscription also keeps the blocks live — an indicator edited
// by someone else appears without a reload, which is the point of the feature.

export interface MeetingStepRole {
  circle: CircleFragment
  role: RoleFragment
}

// Returns both lists at once, from a single subscription: `all` feeds the role
// filter, `filtered` the blocks. Two calls of this hook would open two identical
// subscriptions for one answer — the very waste ENR-08 records (11/08/2026).
export default function useMeetingStepRoles(
  field: 'checklist' | 'indicators',
  filterRolesIds?: string[]
): { all: MeetingStepRole[]; filtered: MeetingStepRole[] } {
  const { circle } = useContext(MeetingContext)!
  const { orgId, orgData } = useOrgContext()

  const { data } = useRolesSubscription({
    skip: !orgId,
    variables: { orgId: orgId!, active: true },
  })

  const all = useMemo(() => {
    if (!orgData || !circle || !data) return []

    const roleById = new Map(data.role.map((r) => [r.id, r]))

    return (
      orgData
        // The circle itself, then its direct children — never deeper. The
        // shared rule, extracted on 11/08 and explained there (REU04-D6).
        .circleWithChildren(circle.id)
        .map((c) => ({ circle: c, role: roleById.get(c.roleId) }))
        .filter((entry): entry is MeetingStepRole => {
          const { role } = entry
          if (!role) return false

          // Base roles are one shared record across every circle: editing their
          // indicators here would spill onto every other circle carrying them,
          // and their holder has no right on them anyway (REU04-D8).
          if (role.base) return false

          // A role with nothing to show is not shown, and nothing says so
          // (REU04-D10): a step opening on empty lines reads as broken.
          return role[field].trim() !== ''
        })
    )
  }, [orgData, circle, data, field])

  const filtered = useMemo(
    () =>
      filterRolesIds?.length
        ? all.filter(({ role }) => filterRolesIds.includes(role.id))
        : all,
    [all, filterRolesIds]
  )

  return { all, filtered }
}
